import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { integrationsAPI } from '../services/integrationsAPI';

interface OAuthCallbackProps {
  platform: string;
}

export const OAuthCallback: React.FC<OAuthCallbackProps> = ({ platform }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code')?.split('#')[0];
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      if (error) {
        // OAuth error
        if (window.opener) {
          window.opener.postMessage({
            type: 'oauth_error',
            error: error
          }, window.location.origin);
        }
        window.close();
        return;
      }

      if (!code || !state) {
        // Missing required parameters
        if (window.opener) {
          window.opener.postMessage({
            type: 'oauth_error',
            error: 'Missing authorization code or state'
          }, window.location.origin);
        }
        window.close();
        return;
      }

      try {
        await integrationsAPI.exchangeOAuthToken(platform, { code, state });

        if (window.opener) {
          window.opener.postMessage({
            type: 'oauth_success',
            platform,
          }, window.location.origin);
        }

        window.close();
      } catch (err: any) {
        // Error during verification
        if (window.opener) {
          window.opener.postMessage({
            type: 'oauth_error',
            error: err.response?.data?.detail || err.message || 'OAuth verification failed'
          }, window.location.origin);
        }
        window.close();
      }
    };

    handleCallback();
  }, [searchParams, platform]);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        gap: 2
      }}
    >
      <CircularProgress size={48} />
      <Typography variant="h6">
        Connecting to {platform}...
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Please wait while we complete the connection process.
      </Typography>
    </Box>
  );
};

// Generic OAuth callback page that determines platform from URL
export const OAuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Check if this is a success/error callback from backend
  const isSuccessCallback = window.location.pathname === '/integrations/success';
  const isErrorCallback = window.location.pathname === '/integrations/error';
  
  // Extract platform from URL path or search params
  const platform = window.location.pathname.split('/').pop() || searchParams.get('platform');

  useEffect(() => {
    if (isSuccessCallback || isErrorCallback) {
      // Handle backend success/error redirect
      const platform = searchParams.get('platform');
      const error = searchParams.get('error');
      
      if (window.opener) {
        if (isSuccessCallback && platform) {
          window.opener.postMessage({
            type: 'oauth_success',
            platform: platform
          }, window.location.origin);
        } else if (isErrorCallback || error) {
          window.opener.postMessage({
            type: 'oauth_error',
            error: error || 'OAuth authentication failed'
          }, window.location.origin);
        }
      }
      
      // Close the popup window
      setTimeout(() => {
        window.close();
      }, 1000);
      
      return;
    }
  }, [searchParams, isSuccessCallback, isErrorCallback]);

  // Show success/error message for backend redirects
  if (isSuccessCallback) {
    const platform = searchParams.get('platform');
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh',
          gap: 2
        }}
      >
        <Alert severity="success">
          Successfully connected to {platform}!
        </Alert>
        <Typography variant="body2" color="text.secondary">
          This window will close automatically...
        </Typography>
      </Box>
    );
  }

  if (isErrorCallback) {
    const error = searchParams.get('error');
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh',
          gap: 2
        }}
      >
        <Alert severity="error">
          Failed to connect: {error || 'Unknown error'}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          This window will close automatically...
        </Typography>
      </Box>
    );
  }

  if (!platform || !['linkedin', 'x', 'instagram', 'facebook'].includes(platform)) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh',
          gap: 2
        }}
      >
        <Alert severity="error">
          Invalid or missing platform parameter
        </Alert>
        <Typography variant="body2">
          This page is used for OAuth callbacks. Please use the proper integration flow.
        </Typography>
      </Box>
    );
  }

  return <OAuthCallback platform={platform} />;
};

export default OAuthCallbackPage; 